/**
 * Las secciones del back office · §A
 *
 * ## Siete secciones con pestañas, y no catorce planas
 *
 * §A son 66 operaciones. Puestas de tú a tú en la barra salen catorce entradas
 * —clubes, leads, impagados, planes, métricas, personas, salud, accesos, procesos,
 * webhooks, interruptores, avisos, catálogos, directorio— y eso no cabe ni con el
 * menú de «Más» que resolvió lo del panel.
 *
 * Aquí **sí** agrupar es correcto, al contrario que allí. En el panel se descartó
 * porque sus diez secciones son diez trabajos distintos de la misma persona; estas se
 * juntan de tres en tres alrededor de una pregunta:
 *
 * 1. **Clubes** — a quién llamo hoy, y todo lo de una cuenta.
 * 2. **Comercial** — quién ha preguntado y en qué va.
 * 3. **Dinero** — quién no nos ha pagado, qué vendemos y cómo va el MRR.
 * 4. **Soporte** — me han llamado: quién es, cómo está su club, quién ha mirado qué.
 * 5. **Operación** — qué se rompió anoche mientras nadie miraba.
 * 6. **Catálogos** — lo que comparten los doscientos clubes.
 * 7. **Directorio** — el censo público y quién reclama su ficha.
 *
 * ## Sin filtro de rol, y por eso no hay `cadencia`
 *
 * El panel ordena su barra por `cadencia` porque tiene cuatro roles y diez secciones
 * que se usan con frecuencias muy distintas. Aquí hay **un** público —nosotros— y
 * `platform: { audit }` no distingue grados de acceso: quien entra, entra a todo.
 * Copiar el mecanismo habría sido copiar la forma sin el problema.
 */

export type SeccionAdmin =
  | 'clubes'
  | 'comercial'
  | 'dinero'
  | 'soporte'
  | 'operacion'
  | 'catalogos'
  | 'directorio';

export const SECCIONES_ADMIN: readonly {
  id: SeccionAdmin;
  etiqueta: string;
}[] = [
  { id: 'clubes', etiqueta: 'Clubes' },
  { id: 'comercial', etiqueta: 'Comercial' },
  { id: 'dinero', etiqueta: 'Dinero' },
  { id: 'soporte', etiqueta: 'Soporte' },
  { id: 'operacion', etiqueta: 'Operación' },
  { id: 'catalogos', etiqueta: 'Catálogos' },
  { id: 'directorio', etiqueta: 'Directorio' },
];

/**
 * «Dinero» · el dinero **de Kiap**, no el de los clubes.
 *
 * Los impagados primero porque es lo que se mira a diario y lo que hay que hacer:
 * llamar. Las métricas al final no por menos importantes, sino porque son para
 * mirar y no para actuar, y quien entra aquí suele venir a actuar.
 */
export type VistaDeDinero = 'avisos' | 'impagados' | 'planes' | 'metricas';

export const VISTAS_DE_DINERO: readonly {
  id: VistaDeDinero;
  etiqueta: string;
}[] = [
  /*
   * §A.2.1 · 27-09-2026. La primera porque es la de actuar: el pago vencido y los
   * que se pasan de su plan, que es a quien hay que llamar hoy.
   */
  { id: 'avisos', etiqueta: 'A quién llamar' },
  { id: 'impagados', etiqueta: 'Quién no ha pagado' },
  { id: 'planes', etiqueta: 'Planes' },
  { id: 'metricas', etiqueta: 'Métricas' },
];

/**
 * «Soporte» · lo que se abre cuando suena el teléfono.
 *
 * Las personas primero: la llamada empieza con «soy Manuel, del Dojang Pascual» y lo
 * primero es encontrarlo. «Quién ha mirado qué» es nuestro propio registro de
 * accesos, y está aquí y no en «Operación» a propósito: se consulta cuando un club
 * pregunta quién ha entrado en su cuenta, que es una conversación de soporte.
 */
export type VistaDeSoporte = 'personas' | 'avisos' | 'salud' | 'accesos';

export const VISTAS_DE_SOPORTE: readonly {
  id: VistaDeSoporte;
  etiqueta: string;
}[] = [
  { id: 'personas', etiqueta: 'Personas' },
  /*
   * «¿Le llegó el aviso?» es **la llamada más frecuente que vamos a recibir** —«no me
   * llegó el correo del recibo»— y por eso va segunda, pegada a las personas. Estuvo
   * un rato en «Operación» porque suena a infraestructura; no lo es: es la
   * conversación que se está teniendo por teléfono en ese momento, y resolverla aquí
   * evita abrir un modo soporte, que es la fricción que hay que reservar para cuando
   * de verdad hay que mirar datos.
   */
  { id: 'avisos', etiqueta: '¿Le llegó el aviso?' },
  { id: 'salud', etiqueta: 'Salud de los clubes' },
  { id: 'accesos', etiqueta: 'Quién ha mirado qué' },
];

/**
 * «Operación» · lo que se rompió mientras nadie miraba.
 *
 * Los procesos primero: son las 2:30 de la mañana de doscientos clubes. Los
 * interruptores al final porque no son una incidencia, son una decisión.
 */
export type VistaDeOperacion =
  'estado' | 'procesos' | 'webhooks' | 'datos' | 'interruptores' | 'equipo';

export const VISTAS_DE_OPERACION: readonly {
  id: VistaDeOperacion;
  etiqueta: string;
}[] = [
  /*
   * §S.1 · 22-09-2026. **La primera**, y es la única de esta sección que se abre con
   * prisa: se entra aquí cuando un club llama diciendo que algo va raro. Las otras
   * cuatro son trabajo que se hace; esta es una pregunta que se contesta en un
   * vistazo, y por eso va donde cae el dedo.
   */
  { id: 'estado', etiqueta: 'Estado' },
  { id: 'procesos', etiqueta: 'Procesos' },
  { id: 'webhooks', etiqueta: 'Webhooks' },
  { id: 'datos', etiqueta: 'Solicitudes de datos' },
  { id: 'interruptores', etiqueta: 'Interruptores' },
  // 12-10-2026. Quién entra aquí. Al final: se toca cuando entra o sale alguien.
  { id: 'equipo', etiqueta: 'Equipo de Kiap' },
];

/**
 * «Catálogos» · lo que comparten los doscientos clubes.
 *
 * Se toca dos veces al año y **cada cambio los afecta a todos**, que es justo lo que
 * hay que tener en la cabeza al entrar aquí. Lo dicen las pantallas.
 */
export type VistaDeCatalogos = 'disciplinas' | 'grados' | 'consentimientos';

export const VISTAS_DE_CATALOGOS: readonly {
  id: VistaDeCatalogos;
  etiqueta: string;
}[] = [
  { id: 'disciplinas', etiqueta: 'Disciplinas' },
  { id: 'grados', etiqueta: 'Sistemas de grados' },
  { id: 'consentimientos', etiqueta: 'Tipos de consentimiento' },
];

/**
 * «Directorio» · el censo público de clubes.
 *
 * Las reclamaciones primero: es lo único con alguien esperando al otro lado.
 */
export type VistaDeDirectorio =
  'reclamaciones' | 'bajas' | 'importar' | 'comparativas';

export const VISTAS_DE_DIRECTORIO: readonly {
  id: VistaDeDirectorio;
  etiqueta: string;
}[] = [
  { id: 'reclamaciones', etiqueta: 'Fichas reclamadas' },
  { id: 'bajas', etiqueta: 'Peticiones de baja' },
  { id: 'importar', etiqueta: 'Importar un censo' },
  /*
   * §A.7 fase 4 · 22-09-2026. Va con el directorio y no con las métricas porque es la
   * otra cara de lo mismo: las métricas dicen cómo va Kiap, y esto dice **cómo van los
   * clubes** — en qué tamaño funciona el producto y en cuál no.
   */
  { id: 'comparativas', etiqueta: 'Comparativas' },
];
